import { useCallback, useRef, useState } from 'react';

import { notification } from 'antd';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import LocationAlertService from '@src/services/LocationAlertService';
import safeExecute from '@src/util/safeExecute';

import { FORM_STATUS, MISSING_PARAMS_ERROR } from '../../../constants';
import type { UsePublicLocationAlertReturn } from '../types';

interface LocationAlertRouteState {
  orgId?: string;
  divId?: string;
  locationId?: string;
}

const getParam = (search: string, key: string): string => {
  const value = new URLSearchParams(search).get(key);
  return value?.trim() || '';
};

export const usePublicLocationAlert = (): UsePublicLocationAlertReturn => {
  const [loading, setLoading] = useState(false);
  const [locationAlertDescription, setLocationAlertDescription] = useState('');
  const [formStatus, setFormStatus] = useState<UsePublicLocationAlertReturn['formStatus']>(null);
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const intl = useIntl();

  const location = useLocation<LocationAlertRouteState | undefined>();
  const orgId = getParam(location.search, 'orgId') || location.state?.orgId || '';
  const divId = getParam(location.search, 'divId') || location.state?.divId || '';
  const locationId = getParam(location.search, 'locationId') || location.state?.locationId || '';
  const missingContext = !orgId || !divId || !locationId;

  const serviceRef = useRef<LocationAlertService | null>(null);
  if (serviceRef.current === null) {
    serviceRef.current = new LocationAlertService();
  }

  const onChangeDescription = useCallback((value: string) => {
    setLocationAlertDescription(value);
  }, []);

  const onSendForm = useCallback(async () => {
    if (missingContext) {
      setSubmitError(new Error(MISSING_PARAMS_ERROR));
      return;
    }

    setLoading(true);
    setSubmitError(null);

    try {
      await safeExecute(
        async () => {
          await serviceRef.current!.submitDescription(
            orgId,
            divId,
            locationId,
            locationAlertDescription,
          );
        },
        'LocationAlertService.submitDescription',
        { orgId, divId, locationId },
      );
      setFormStatus(FORM_STATUS.SUBMITTED);
    } catch (error) {
      const nextError = error instanceof Error ? error : new Error(String(error));
      setSubmitError(nextError);
      setFormStatus(FORM_STATUS.ERROR);
      notification.error({
        message: intl.formatMessage({ id: 'location.alert.error.submit' }),
      });
    } finally {
      setLoading(false);
    }
  }, [divId, intl, locationAlertDescription, locationId, missingContext, orgId]);

  return {
    formStatus,
    loading,
    locationAlertDescription,
    missingContext,
    onChangeDescription,
    onSendForm,
    submitError,
  };
};
